import i18n from "~/i18n";

const customRound = function (value, decimal) {
  let x = value * Math.pow(10, decimal + 1);
  let diff = x % 10;
  if (diff >= 5) {
    x += 10;
  } else if (diff >= 4 && diff < 5) {
    let temp = value * Math.pow(10, 10 + decimal + 1);
    if (temp % Math.pow(10, 10) >= 9999999995) {
      x += 10;
    }
  }
  x -= diff;
  return Number(x / Math.pow(10, decimal + 1));
};

const round = (value, decimals) => {
  const is_positive = Number(value) > 0 ? true : false;
  value = is_positive ? value : -1 * value;
  let rounded_value = Number(Math.round(value + "e" + decimals) + "e-" + decimals);
  return is_positive > 0 ? rounded_value : -1 * rounded_value;
};

export class InvoiceItemCalculator {
  constructor(
    taxRates = {},
    params = {
      secondTaxIsEnabled: false,
      secondTaxIsCompound: false,
      decimalSpaces: 2,
      discountRate: 0,
    }
  ) {
    this.taxRates = taxRates;
    this.secondTaxIsEnabled = params.secondTaxIsEnabled;
    this.secondTaxIsCompound = params.secondTaxIsCompound;
    this.decimalSpaces = params.decimalSpaces;
    this.discountRate = params.discountRate;
  }

  updateItemTotal = (item) => {
    this.discountRate = item.discount_rate;

    let result = { ...item };
    result.quantity = Number(result.quantity).toFixed(2);
    result.unit_cost = Number(result.unit_cost).toFixed(this.decimalSpaces);
    result.subtotal = customRound(
      Number(result.unit_cost * result.quantity),
      this.decimalSpaces //difference from old v7, old v7 round always at 2 decimal spaces
    );

    if (this.discountRate) {
      const preparedDiscountRate = round(this.discountRate, 2);
      const discountRate =
        Number(preparedDiscountRate) < 0 || Number(preparedDiscountRate) > 100 ? 0 : Number(preparedDiscountRate);
      result.unit_discount = (Number(result.unit_cost) * discountRate) / 100;
      result.discount = result.subtotal * (discountRate / 100);
      result.discounted_subtotal = Number(result.subtotal) - Number(result.discount);
    } else {
      result.unit_discount = (0).toFixed(this.decimalSpaces);
      result.discount = (0).toFixed(this.decimalSpaces);
      result.discounted_subtotal = result.subtotal;
    }

    result.first_tax_value = 0;
    result.second_tax_value = 0;
    if (item.first_tax_rate_id) {
      const rate = this.getTaxRatePercentage(item.first_tax_rate_id);
      if (rate) {
        result.first_unit_tax_value = ((Number(result.unit_cost) - Number(result.unit_discount)) * rate) / 100;
        result.first_tax_value = customRound(result.quantity * result.first_unit_tax_value, 5);
      }
    } else {
      result.first_unit_tax_value = Number("0.000").toFixed(5);
      result.first_tax_value = Number("0.000").toFixed(5);
    }

    if ((this.secondTaxIsEnabled || item.second_tax_is_enabled) && item.second_tax_rate_id) {
      const rate = this.getTaxRatePercentage(item.second_tax_rate_id);
      if (rate) {
        if (!this.secondTaxIsCompound) {
          result.second_unit_tax_value = ((Number(result.unit_cost) - Number(result.unit_discount)) * rate) / 100;
        } else {
          result.second_unit_tax_value =
            ((Number(result.unit_cost) - Number(result.unit_discount) + Number(result.first_tax_value)) * rate) / 100;
        }
        result.second_tax_value = customRound(result.quantity * result.second_unit_tax_value, 5);
      }
    } else {
      result.second_unit_tax_value = Number("0.000").toFixed(5);
      result.second_tax_value = Number("0.000").toFixed(5);
    }

    result.total = 0;
    result.total += result.subtotal ? Number(result.subtotal) : 0;
    result.total += result.first_tax_value ? Number(result.first_tax_value) : 0;
    result.total += result.second_tax_value ? Number(result.second_tax_value) : 0;

    return result;
  };

  updateItemUnitCost = (item) => {
    let result = { ...item };
    result.unit_cost = (Number(result.subtotal) / Number(result.quantity)).toFixed(this.decimalSpaces);
    return this.updateItemTotal(result);
  };

  getTaxRatePercentage = (id) => {
    if (this.taxRates[id]) {
      return this.taxRates[id]?.percentage;
    }
    return null;
  };
}

export class InvoiceCalculator {
  constructor(taxRates = {}, currency = null) {
    this.taxRates = taxRates;
    if (currency) {
      this.currency = currency;
      this.decimalSpaces = currency.decimal_spaces;
    } else {
      this.currency = null;
      this.decimalSpaces = 2;
    }
  }

  recalculateInvoice = (items) => {
    let result = {
      subtotal: 0,
      discount: 0,
      discounted_subtotal: 0,
      total: 0,
      rounded_total: 0,
      rounding_difference: 0,
      taxes: [],
    };

    let subtotal = 0;
    let taxes = {};
    let discount = 0;

    items.forEach((item) => {
      if (item.subtotal) {
        subtotal += Number(item.subtotal);
      }

      if (item.first_tax_rate_id) {
        if (!taxes[item.first_tax_rate_id]) {
          taxes[item.first_tax_rate_id] = 0;
        }
        taxes[item.first_tax_rate_id] += item.first_tax_value ? Number(item.first_tax_value) : 0;
      }

      if (item.second_tax_rate_id) {
        if (!taxes[item.second_tax_rate_id]) {
          taxes[item.second_tax_rate_id] = 0;
        }
        taxes[item.second_tax_rate_id] += item.second_tax_rate_id ? Number(item.second_tax_value) : 0;
      }

      if (item.discount) {
        discount += Number(item.discount);
      }
    });

    result.subtotal = Number(subtotal).toFixed(this.decimalSpaces);

    let rounded_total_discount = customRound(discount, this.decimalSpaces);
    result.discount = discount ? "-" + rounded_total_discount.toFixed(this.decimalSpaces) : null;
    result.discounted_subtotal = Number(result.subtotal) - Number(rounded_total_discount);

    result.total = Number(result.discounted_subtotal);

    result.rounded_total = result.total;
    result.rounding_difference = 0;

    if (this.currency && this.currency.decimal_rounding) {
      let rounding_step = 1 / this.currency.decimal_rounding;
      result.rounded_total = (Math.round(result.total * rounding_step) / rounding_step).toFixed(this.decimalSpaces);
      result.rounding_difference = (result.rounded_total - result.total).toFixed(this.decimalSpaces);
    }

    Object.keys(taxes).forEach((tax_rate_id) => {
      let tax_rate = this.taxRates[tax_rate_id];
      let tax = customRound(taxes[tax_rate_id], this.decimalSpaces);
      result.taxes.push({
        name: tax_rate ? tax_rate.name : i18n.t("Tax"),
        amount: tax.toFixed(this.decimalSpaces),
        percentage: tax_rate ? tax_rate.percentage : null,
      });
      result.total += tax;
    });

    result.total = Number(result.total).toFixed(this.decimalSpaces);

    return result;
  };
}

export const INVOICE_OBJECT_DUE_AFTER_0_DAYS = 0;
export const INVOICE_OBJECT_DUE_AFTER_10_DAYS = 10;
export const INVOICE_OBJECT_DUE_AFTER_15_DAYS = 15;
export const INVOICE_OBJECT_DUE_AFTER_20_DAYS = 20;
export const INVOICE_OBJECT_DUE_AFTER_30_DAYS = 30;
export const INVOICE_OBJECT_DUE_AFTER_60_DAYS = 60;

export const INVOICE_OBJECT_DUE_AFTER_MAP = {
  [INVOICE_OBJECT_DUE_AFTER_0_DAYS]: i18n.t(`Due on issue`),
  [INVOICE_OBJECT_DUE_AFTER_10_DAYS]: i18n.t(`10 days after issue`),
  [INVOICE_OBJECT_DUE_AFTER_15_DAYS]: i18n.t(`15 days after issue`),
  [INVOICE_OBJECT_DUE_AFTER_20_DAYS]: i18n.t(`20 days after issue`),
  [INVOICE_OBJECT_DUE_AFTER_30_DAYS]: i18n.t(`30 days after issue`),
  [INVOICE_OBJECT_DUE_AFTER_60_DAYS]: i18n.t(`60 days after issue`),
};

export const RFC_EMAIL_SPLITTER_REGEX = /[^< ]+(?=>)/gm;
export const RFC_EMAIL_VALIDATOR_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
